import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Building2, QrCode, CheckCircle2, AlertTriangle,
  RefreshCw, Banknote, FileText,
} from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { paymentConfig as api, type PaymentConfig, type ApiError } from '../../services/api';

/* ── styles ── */
const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box',
};
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase',
};
const textarea: React.CSSProperties = {
  ...inp, resize: 'vertical', minHeight: 90, fontFamily: 'inherit', lineHeight: 1.55,
};

const EMPTY: Partial<PaymentConfig> = {
  accountName: '', accountNumber: '', ifscCode: '', bankName: '', branchName: '',
  upiId: '', paymentInstructions: '',
  acceptBankTransfer: true, acceptUpi: true, acceptCash: false, acceptCheque: false,
};

const ToggleSwitch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; desc?: string }> = ({ checked, onChange, label, desc }) => (
  <div
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nx-border)', background: checked ? 'rgba(26,108,246,0.04)' : 'transparent', cursor: 'pointer', transition: 'all 0.15s' }}
    onClick={() => onChange(!checked)}
  >
    <div>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>{label}</div>
      {desc && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{desc}</div>}
    </div>
    <div style={{
      width: 38, height: 22, borderRadius: 11, position: 'relative', flexShrink: 0,
      background: checked ? 'var(--nx-blue)' : 'var(--nx-surface-3)',
      border: `1px solid ${checked ? 'var(--nx-blue)' : 'var(--nx-border)'}`,
      transition: 'background 0.2s, border-color 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: 2, left: checked ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%',
        background: checked ? '#fff' : 'var(--nx-text-muted)',
        transition: 'left 0.2s',
      }} />
    </div>
  </div>
);

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(26,108,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nx-blue)', flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)' }}>{title}</div>
      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{subtitle}</div>
    </div>
  </div>
);

const PaymentConfigPage: React.FC = () => {
  const { user } = useAuth();
  const sid = user?.societyId ?? null;

  const [form, setForm]     = useState<Partial<PaymentConfig>>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!sid) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await api.get(sid);
      setForm({ ...EMPTY, ...data });
    } catch {
      /* no config yet — stay with defaults */
    } finally {
      setLoading(false);
    }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  const set = (key: keyof PaymentConfig, value: string | boolean) =>
    setForm(f => ({ ...f, [key]: value }));

  const save = async () => {
    if (!sid) return;
    setMsg(null);

    if (form.acceptBankTransfer && (!form.accountName?.trim() || !form.accountNumber?.trim() || !form.ifscCode?.trim())) {
      setMsg({ type: 'error', text: 'Account Holder Name, Account Number, and IFSC Code are required when Bank Transfer is enabled.' });
      return;
    }
    if (form.acceptUpi && !form.upiId?.trim()) {
      setMsg({ type: 'error', text: 'UPI ID is required when UPI is enabled.' });
      return;
    }

    setSaving(true);
    try {
      await api.save(sid, form);
      setMsg({ type: 'success', text: 'Payment settings saved successfully.' });
    } catch (e) {
      setMsg({ type: 'error', text: (e as ApiError).message ?? 'Failed to save settings.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Payment Configuration" subtitle="Configure how residents pay their dues" icon={<CreditCard size={18} />}>
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading…</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Payment Configuration"
      subtitle="Configure how residents can pay their monthly maintenance dues"
      icon={<CreditCard size={18} />}
      actions={
        <button className="nx-btn nx-btn-primary nx-btn-sm" onClick={save} disabled={saving} style={{ gap: 5 }}>
          {saving ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : null}
          Save Changes
        </button>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
        {/* Left — form sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Accepted Payment Modes */}
          <div className="nx-card" style={{ padding: 20 }}>
            <SectionHeader icon={<CreditCard size={16} />} title="Accepted Payment Modes" subtitle="Enable the methods residents can use to pay" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ToggleSwitch
                checked={!!form.acceptBankTransfer}
                onChange={v => set('acceptBankTransfer', v)}
                label="Bank Transfer (NEFT / RTGS / IMPS)"
                desc="Residents transfer directly to society bank account"
              />
              <ToggleSwitch
                checked={!!form.acceptUpi}
                onChange={v => set('acceptUpi', v)}
                label="UPI"
                desc="Google Pay, PhonePe, Paytm, BHIM etc."
              />
              <ToggleSwitch
                checked={!!form.acceptCash}
                onChange={v => set('acceptCash', v)}
                label="Cash"
                desc="In-person cash payment at the office"
              />
              <ToggleSwitch
                checked={!!form.acceptCheque}
                onChange={v => set('acceptCheque', v)}
                label="Cheque / DD"
                desc="Cheque or demand draft in favour of the society"
              />
            </div>
          </div>

          {/* Bank Account Details */}
          {form.acceptBankTransfer && (
            <div className="nx-card" style={{ padding: 20 }}>
              <SectionHeader icon={<Building2 size={16} />} title="Bank Account Details" subtitle="Shown to residents when they pay via bank transfer" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Account Holder Name *</label>
                  <input style={inp} placeholder="e.g. Sunshine Heights RWA"
                    value={form.accountName ?? ''}
                    onChange={e => set('accountName', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Account Number *</label>
                  <input style={inp} placeholder="12-digit account number"
                    value={form.accountNumber ?? ''}
                    onChange={e => set('accountNumber', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>IFSC Code *</label>
                  <input style={{ ...inp, textTransform: 'uppercase' }} placeholder="e.g. SBIN0001234"
                    value={form.ifscCode ?? ''}
                    onChange={e => set('ifscCode', e.target.value.toUpperCase())} />
                </div>
                <div>
                  <label style={lbl}>Bank Name</label>
                  <input style={inp} placeholder="e.g. State Bank of India"
                    value={form.bankName ?? ''}
                    onChange={e => set('bankName', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Branch Name</label>
                  <input style={inp} placeholder="e.g. Koramangala Branch"
                    value={form.branchName ?? ''}
                    onChange={e => set('branchName', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* UPI Details */}
          {form.acceptUpi && (
            <div className="nx-card" style={{ padding: 20 }}>
              <SectionHeader icon={<QrCode size={16} />} title="UPI Details" subtitle="Residents can pay directly to this UPI ID" />
              <div>
                <label style={lbl}>UPI ID *</label>
                <input style={inp} placeholder="e.g. sunshinehts@sbi or 9876543210@paytm"
                  value={form.upiId ?? ''}
                  onChange={e => set('upiId', e.target.value)} />
                <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 5 }}>
                  This will be displayed to residents as a scannable link. Make sure it is registered and active.
                </p>
              </div>
            </div>
          )}

          {/* Payment Instructions */}
          <div className="nx-card" style={{ padding: 20 }}>
            <SectionHeader icon={<FileText size={16} />} title="Payment Instructions" subtitle="Custom message shown to residents on their invoice" />
            <div>
              <label style={lbl}>Instructions (optional)</label>
              <textarea
                style={textarea}
                placeholder="e.g. Please mention your flat number in the transfer remarks. Payments received after the 10th will attract a late fee of 2%."
                value={form.paymentInstructions ?? ''}
                onChange={e => set('paymentInstructions', e.target.value)}
              />
              <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 5 }}>
                This text is printed on every invoice PDF sent to residents.
              </p>
            </div>
          </div>
        </div>

        {/* Right — live preview */}
        <div style={{ position: 'sticky', top: 88 }}>
          <div className="nx-card" style={{ padding: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>
              Resident Preview
            </p>
            <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 14, lineHeight: 1.55 }}>
              This is how payment details appear on residents' invoices.
            </p>

            <div style={{ background: 'var(--nx-surface-2)', borderRadius: 10, padding: 14, border: '1px solid var(--nx-border)', fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 10, fontSize: 13 }}>
                💳 How to Pay
              </div>

              {form.acceptBankTransfer && (form.accountName || form.accountNumber) && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 4 }}>Bank Transfer</div>
                  {form.accountName && <div style={{ color: 'var(--nx-text-muted)' }}>Name: <strong style={{ color: 'var(--nx-text-primary)' }}>{form.accountName}</strong></div>}
                  {form.accountNumber && <div style={{ color: 'var(--nx-text-muted)' }}>A/C: <strong style={{ color: 'var(--nx-text-primary)' }}>{form.accountNumber}</strong></div>}
                  {form.ifscCode && <div style={{ color: 'var(--nx-text-muted)' }}>IFSC: <strong style={{ color: 'var(--nx-text-primary)' }}>{form.ifscCode}</strong></div>}
                  {form.bankName && <div style={{ color: 'var(--nx-text-muted)' }}>Bank: <strong style={{ color: 'var(--nx-text-primary)' }}>{form.bankName}</strong></div>}
                </div>
              )}

              {form.acceptUpi && form.upiId && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 4 }}>UPI</div>
                  <div style={{ display: 'inline-block', background: 'rgba(26,108,246,0.1)', color: 'var(--nx-blue)', padding: '3px 10px', borderRadius: 6, fontWeight: 600, letterSpacing: '0.02em' }}>
                    {form.upiId}
                  </div>
                </div>
              )}

              {(form.acceptCash || form.acceptCheque) && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 4 }}>Also accepted</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {form.acceptCash && <span style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 500 }}>Cash</span>}
                    {form.acceptCheque && <span style={{ background: 'rgba(109,40,217,0.1)', color: '#6D28D9', padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 500 }}>Cheque / DD</span>}
                  </div>
                </div>
              )}

              {form.paymentInstructions && (
                <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 7, fontSize: 11, color: 'var(--nx-text-secondary)', lineHeight: 1.55 }}>
                  {form.paymentInstructions}
                </div>
              )}

              {!form.acceptBankTransfer && !form.acceptUpi && !form.acceptCash && !form.acceptCheque && (
                <div style={{ color: 'var(--nx-text-muted)', fontSize: 12 }}>No payment modes enabled yet.</div>
              )}
            </div>
          </div>

          {/* Save status message */}
          {msg && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: msg.type === 'success' ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
              {msg.type === 'success'
                ? <CheckCircle2 size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                : <AlertTriangle size={14} style={{ color: '#DC2626', flexShrink: 0 }} />}
              <span style={{ fontSize: 12, fontWeight: 500, color: msg.type === 'success' ? '#10B981' : '#DC2626' }}>
                {msg.text}
              </span>
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <button
              className="nx-btn nx-btn-primary"
              onClick={save}
              disabled={saving}
              style={{ width: '100%', justifyContent: 'center', gap: 6 }}
            >
              {saving
                ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                : <><Banknote size={13} /> Save Payment Settings</>}
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PaymentConfigPage;
